import React, { useEffect, useMemo, useState } from 'react';
import ThreeDLogoMark from '../common/ThreeDLogoMark';

const VIEWBOX_WIDTH = 1440;
const VIEWBOX_HEIGHT = 900;
const HUB_CENTER = { x: 720, y: 404 };
const BRANCH_DRAW_DURATION_MS = 2200;

const HERO_NODES = [
  {
    id: 'registry',
    buttonX: 72,
    buttonY: 150,
    branchX: 324,
    branchY: 170,
    laneY: 334,
    bend: 124,
    label: 'Central Registry',
    meta: 'Schema-first skill records',
    kind: 'foundation',
    side: 'left',
    accent: '#00d4aa',
    explanation: 'Syniq maintains a unified registry where all skills are stored with structured schemas, making them discoverable and version-controlled.',
  },
  {
    id: 'importer',
    buttonX: 72,
    buttonY: 332,
    branchX: 310,
    branchY: 372,
    laneY: 404,
    bend: 92,
    label: 'Bulk Importer',
    meta: 'Auto-parse markdown skills at scale',
    kind: 'foundation',
    side: 'left',
    accent: '#00e5c3',
    explanation: 'Import hundreds of markdown-based skills at once. Syniq automatically parses and structures them for immediate use.',
  },
  {
    id: 'category',
    buttonX: 72,
    buttonY: 532,
    branchX: 336,
    branchY: 560,
    laneY: 484,
    bend: 110,
    label: 'Category System',
    meta: 'Counts, filters, grouped lanes',
    kind: 'foundation',
    side: 'left',
    accent: '#7ffff0',
    explanation: 'Organize skills into categories with smart filtering and grouping, making it easy to navigate large skill libraries.',
  },
  {
    id: 'semantic',
    buttonX: 1368,
    buttonY: 150,
    branchX: 1116,
    branchY: 170,
    laneY: 334,
    bend: -124,
    label: 'Semantic Search',
    meta: 'Embeddings and similarity ranking',
    kind: 'intelligence',
    side: 'right',
    accent: '#7ffff0',
    explanation: 'Syniq uses AI embeddings to match tasks with the most relevant skills based on meaning, not just keywords.',
  },
  {
    id: 'selection',
    buttonX: 1368,
    buttonY: 332,
    branchX: 1130,
    branchY: 372,
    laneY: 404,
    bend: -92,
    label: 'Ranked Selection',
    meta: 'Constraints, trust, approvals',
    kind: 'intelligence',
    side: 'right',
    accent: '#46bda9',
    explanation: 'Skills are ranked by relevance and filtered by trust levels, with human approval gates for critical decisions.',
  },
  {
    id: 'transparency',
    buttonX: 1368,
    buttonY: 532,
    branchX: 1104,
    branchY: 560,
    laneY: 484,
    bend: -110,
    label: 'Decision Transparency',
    meta: 'Why chosen, score, rejected options',
    kind: 'intelligence',
    side: 'right',
    accent: '#00d4aa',
    explanation: 'Every skill selection shows why it was chosen, its confidence score, and what alternatives were considered.',
  },
];

function pathFromTo(from, to, bendAmount) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const distance = Math.hypot(dx, dy) || 1;
  const normalX = -dy / distance;
  const normalY = dx / distance;

  const controlOne = {
    x: from.x + dx * 0.32 + normalX * bendAmount,
    y: from.y + dy * 0.18 + normalY * bendAmount,
  };

  const controlTwo = {
    x: from.x + dx * 0.72 + normalX * (bendAmount * 0.56),
    y: from.y + dy * 0.88 + normalY * (bendAmount * 0.56),
  };

  return `M ${from.x} ${from.y} C ${controlOne.x} ${controlOne.y}, ${controlTwo.x} ${controlTwo.y}, ${to.x} ${to.y}`;
}

function buildNodePath(node) {
  const from = {
    x: node.side === 'left' ? HUB_CENTER.x - 142 : HUB_CENTER.x + 142,
    y: node.laneY,
  };
  const to = {
    x: node.branchX,
    y: node.branchY,
  };

  return pathFromTo(from, to, node.bend);
}

function getNodePositionStyle(node, index) {
  const positionStyle = {
    '--node-accent': node.accent,
    // Delay nodes significantly so branches draw first (branches take ~2.2s)
    '--hero-node-delay': `${1.8 + index * 0.18}s`,
    '--hero-node-enter-x': node.side === 'left' ? '-40px' : '40px',
  };

  if (node.side === 'left') {
    positionStyle.left = '0';
  } else {
    positionStyle.right = '0';
  }

  return positionStyle;
}

export default function HeroOrchestrationScene() {
  const [expandedNode, setExpandedNode] = useState(null);

  const activeNode = useMemo(
    () => HERO_NODES.find((node) => node.id === expandedNode) || null,
    [expandedNode]
  );

  const links = useMemo(
    () =>
      HERO_NODES.map((node) => ({
        ...node,
        path: buildNodePath(node),
      })),
    []
  );


  const handleNodeToggle = (nodeId) => {
    setExpandedNode((current) => (current === nodeId ? null : nodeId));
  };
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setExpandedNode(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className={`hero-orchestration-scene ${expandedNode ? 'has-active-node' : ''}`} aria-label="Syniq orchestration overview">
      <div className="hero-orchestration-mesh hero-orchestration-mesh-primary" />
      <div className="hero-orchestration-mesh hero-orchestration-mesh-secondary" />
      <div className="hero-orchestration-grid" />
      <div className="hero-orchestration-vignette" />

      <svg
        className="hero-orchestration-svg"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMin slice"
      >
        <g className="hero-orchestration-parallax hero-orchestration-parallax-near">
          {links.map((node, index) => (
            <g key={`${node.id}-link`} style={{ '--line-accent': node.accent, '--branch-delay': `${0.12 + index * 0.18}s` }}>
              <path
                className="hero-orchestration-link"
                d={node.path}
                pathLength="1"
              />
              <circle className="hero-orchestration-link-terminal" cx={node.branchX} cy={node.branchY} r="7" />
            </g>
          ))}
          {HERO_NODES.map((node, index) => (
            <foreignObject
              key={node.id}
              x={node.side === 'left' ? node.buttonX : node.buttonX - 360}
              y={node.buttonY - 20}
              width="360"
              height="100"
              style={{ overflow: 'visible' }}
            >
              <div className={`hero-orchestration-node-wrapper hero-orchestration-node-${node.side}`} style={{ position: 'relative', width: '100%', height: '100%' }}>
                <button
                  type="button"
                  className={`hero-orchestration-node hero-orchestration-node-button hero-orchestration-node-${node.kind}${expandedNode === node.id ? ' is-expanded is-active' : ''}`}
                  style={getNodePositionStyle(node, index)}
                  aria-label={`${expandedNode === node.id ? 'Hide' : 'Show'} Syniq details for ${node.label}`}
                  aria-pressed={expandedNode === node.id}
                  onClick={() => handleNodeToggle(node.id)}
                >
                  <span className="hero-orchestration-node-marker" aria-hidden="true">
                    <span className="hero-orchestration-node-marker-core" />
                  </span>
                  <span className="hero-orchestration-node-copy">
                    <strong>{node.label}</strong>
                    <small>{node.meta}</small>
                  </span>
                </button>
              </div>
            </foreignObject>
          ))}

          {/* Render the inspector as the very last SVG element so it overlays everything without sorting nodes */}
          {activeNode && (
            <foreignObject
              x={activeNode.side === 'left' ? activeNode.buttonX : activeNode.buttonX - 360}
              y={activeNode.buttonY - 20}
              width="360"
              height="600"
              style={{ overflow: 'visible', zIndex: 50, pointerEvents: 'none' }}
            >
              <div className={`hero-orchestration-node-wrapper hero-orchestration-node-${activeNode.side}`} style={{ position: 'relative', width: '100%', height: '100%', pointerEvents: 'none' }}>
                <aside
                  className="hero-orchestration-inspector inline-inspector is-visible"
                  aria-live="polite"
                  style={{ pointerEvents: 'auto', top: '70px', position: 'absolute' }}
                >
                  <div className="hero-orchestration-inspector-header">
                    <span className="hero-orchestration-inspector-kicker">Selected layer</span>
                    <button
                      type="button"
                      className="hero-orchestration-inspector-close"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedNode(null);
                      }}
                      aria-label="Close selected layer details"
                    >
                      x
                    </button>
                  </div>
                  <strong>{activeNode.label}</strong>
                  <span className="hero-orchestration-inspector-meta">
                    {activeNode.meta}
                  </span>
                  <p>{activeNode.explanation}</p>
                </aside>
              </div>
            </foreignObject>
          )}
        </g>
      </svg>

      <div className="hero-orchestration-core">
        <div className="hero-orchestration-core-border">
          <div className="hero-orchestration-core-ring hero-orchestration-core-ring-outer" />
          <div className="hero-orchestration-core-ring hero-orchestration-core-ring-inner" />
        </div>
        <div className="hero-orchestration-core-shell" />
        <div className="hero-orchestration-core-caption">
          <span>Syniq Orchestrator</span>
          <strong>Import. Rank. Route. Explain.</strong>
        </div>
        <ThreeDLogoMark className="hero-orchestration-logo-mark" depth={14} glow="soft" />
      </div>
    </div>
  );
}
